import { registerMutation, useMutation } from 'data-fetching-client';
import { useParams } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { useCallback, useState } from 'react';
import { H2 } from 'UIComponents/elements/headings';
import UIButton from 'UIComponents/button/UIButton';
import { Flex } from 'foundations-components/components/Flex';
import { getClient } from '../data/client';
import Loading from '../components/Loading';
import { getApiUrl } from '../utils/urls';
import { captureException } from '../utils/captureException';
import LocaleFormattedMessage from '../components/locales/LocaleFormattedMessage';
import { ErrorMessage } from '../components/ErrorMessage';
import { getApolloErrorStatusCode } from '../utils/getApolloErrorStatusCode';
import { INTERACTIONS, TRACKING_SCREENS } from '../constants/TrackingConstants';
import { trackInteraction } from '../utils/usageTracker';
import { useTrackPageView } from '../hooks/use-tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const submitVerifyRequestMutation = 'submitVerifyRequestMutation';
const SUBMIT_VERIFY_REQUEST_MUTATION = registerMutation({
  fieldName: submitVerifyRequestMutation,
  args: ['token'],
  fetcher: ({
    token
  }) => getClient().post(`${getApiUrl()}/verify/${token}`)
});
function VerifyContainer() {
  const {
    token
  } = useParams();
  const [verified, setVerified] = useState(false);
  useTrackPageView({
    screen: TRACKING_SCREENS.VERIFY_REQUEST
  });
  const [verifyRequestMutation, {
    loading,
    error
  }] = useMutation(SUBMIT_VERIFY_REQUEST_MUTATION, {
    onCompleted: () => {
      setVerified(true);
    },
    onError: err => {
      captureException(err, ['Error verifying data privacy request']);
    }
  });
  const verifyRequest = useCallback(() => {
    void verifyRequestMutation({
      variables: {
        token
      }
    });
    trackInteraction({
      interaction: INTERACTIONS.VERIFY_REQUEST
    });
  }, [token, verifyRequestMutation]);
  if (loading) {
    return /*#__PURE__*/_jsx(Loading, {});
  }
  if (error) {
    const statusCode = getApolloErrorStatusCode(error);
    if (statusCode === 429) {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.rateLimitUI.title"
        }),
        body: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.rateLimitUI.body"
        })
      });
    } else {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.tokenExpired.title"
        }),
        body: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.error.tokenExpired.body"
        })
      });
    }
  }
  if (verified) {
    return /*#__PURE__*/_jsxs(Flex, {
      minBlockSize: "50vh",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.body1"
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "VERIFY_LOAD",
    children: /*#__PURE__*/_jsxs(Flex, {
      minBlockSize: "50vh",
      alignItems: "center",
      justifyContent: "center",
      "data-test-id": "verify-token-container",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.beforeSubmitted.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.beforeSubmitted.bodyPartOne"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.beforeSubmitted.bodyPartTwo"
        })
      }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary",
        onClick: verifyRequest,
        children: /*#__PURE__*/_jsx(LocaleFormattedMessage, {
          message: "app.verify.beforeSubmitted.cta"
        })
      })]
    })
  });
}
export default VerifyContainer;